import { useUpdatePermissions } from "api/permissions/usePermissions";
import AuthenticationContext from "config/context/authenticationContext";
import GuiContext from "config/context/guiContext";
import TableContext from "config/context/tableContext";
import { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { testPrivateKey, validateMultiFormatFile, validateURL } from "utils/validations";
import { useGetDatabaseStructureService } from "features/compositions/services/useGetDatabaseStructureService";

const initialServerStatus = {
    response: null,
    loading: false,
    error: null,
}

export const useCreateComposition = ({ privateKey, tables, structs }) => {
    const navigate = useNavigate();
    const [response, setResponse] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const { updateDatabaseStructure } = useUpdateDatabaseStructure();
    const { updatePermissions } = useUpdatePermissions();
    const { setActiveComposition } = useContext(TableContext);
    const {
        selected: { selectedAccount, selectedServer },
        URL,
    } = useContext(GuiContext);

    const setComposition = async () => {

        setLoading(true);

        try {
            const res = await fetch(`${URL}/api/database/add_composition`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify({
                    account_name: selectedAccount || "",
                    private_key: privateKey,
                    server_url: selectedServer || "",
                    tables: tables,
                    structs: structs,
                }),
            });

            const data = await res.json();
            setResponse(data);
            updateDatabaseStructure();
            updatePermissions();
            setActiveComposition(data?.created);
            setTimeout(() => {
                navigate("/compositions");
            }, 1600);

            if (!data?.status) {
                setError(data?.message);
            }
        } catch (err) {
            setError(err.message || 'An error occurred');
        } finally {
            setLoading(false);
        }
    };

    const resetServerStatus = () => {
        setLoading(initialServerStatus.loading);
        setResponse(initialServerStatus.response);
        setError(initialServerStatus.error);
    }

    return {
        response,
        loading,
        error,
        setComposition,
        resetServerStatus
    };
};

export const useDropComposition = ({ privateKey, compositionName }) => {
    const [response, setResponse] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const { updateDatabaseStructure } = useUpdateDatabaseStructure();
    const { tableStructures, setActiveComposition } = useContext(TableContext);

    const {
        selected: { selectedAccount, selectedServer },
        URL,
    } = useContext(GuiContext);

    const dropComposition = async () => {
        setLoading(true);

        try {
            const res = await fetch(`${URL}/api/database/drop_composition`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify({
                    account_name: selectedAccount,
                    composition_name: compositionName,
                    private_key: privateKey,
                    server_url: selectedServer,
                }),
            });

            const data = await res.json();
            setResponse(data);
            updateDatabaseStructure();
            setActiveComposition(tableStructures.find(item => item?.name !== compositionName)?.name || "");

            if (!data?.status) {
                setError(data?.message);
            }
        } catch (err) {
            setError(err.message || 'An error occurred');
        } finally {
            setLoading(false);
        }
    };

    const resetServerStatus = () => {
        setLoading(initialServerStatus.loading);
        setResponse(initialServerStatus.response);
        setError(initialServerStatus.error);
    }

    return {
        response,
        loading,
        error,
        dropComposition,
        resetServerStatus
    };
};

export const useClearComposition = ({ privateKey, compositionName, permissionName }) => {
    const [response, setResponse] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const { updateDatabaseStructure } = useUpdateDatabaseStructure();
    const { activeComposition, setActiveComposition } = useContext(TableContext);
    const {
        selected: { selectedAccount, selectedServer },
        URL,
    } = useContext(GuiContext);

    const clearComposition = async () => {
        setLoading(true);

        try {
            const res = await fetch(`${URL}/api/database/clear_composition`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify({
                    account_name: selectedAccount,
                    composition_name: compositionName,
                    private_key: privateKey,
                    server_url: selectedServer,
                    permission: permissionName,
                }),
            });

            const data = await res.json();
            setResponse(data);
            // updateDatabaseStructure();
            setActiveComposition(activeComposition);

            if (!data?.status) {
                setError(data?.message);
            }
        } catch (err) {
            setError(err.message || 'An error occurred');
        } finally {
            setLoading(false);
        }
    };

    const resetServerStatus = () => {
        setLoading(initialServerStatus.loading);
        setResponse(initialServerStatus.response);
        setError(initialServerStatus.error);
    }

    return {
        response,
        loading,
        error,
        clearComposition,
        resetServerStatus
    };
};

export const useSaveComposition = ({ privateKey, permission, actions, encryption }) => {
    const [response, setResponse] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const { updateDatabaseStructure } = useUpdateDatabaseStructure();

    const {
        selected: { selectedAccount, selectedServer },
        URL,
    } = useContext(GuiContext);

    const setComposition = async () => {

        setLoading(true);

        try {
            const res = await fetch(`${URL}/api/database/push_transaction`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify({
                    server_url: selectedServer || "",
                    account_name: selectedAccount,
                    permission: permission,
                    private_key: privateKey,
                    actions: actions,
                    encrypted: encryption,
                }),
            });

            const data = await res.json();
            setResponse(data);
            updateDatabaseStructure();

            if (!data?.status) {
                setError(data?.message);
            }
        } catch (err) {
            setError(err.message || 'An error occurred');
        } finally {
            setLoading(false);
        }
    };

    const resetServerStatus = () => {
        setLoading(initialServerStatus.loading);
        setResponse(initialServerStatus.response);
        setError(initialServerStatus.error);
    }

    return {
        response,
        loading,
        error,
        setComposition,
        resetServerStatus
    };
};

export const useImportRedisDatabase = ({
    private_key,
    redis_host,
    redis_port,
    redis_password,
    composition
}) => {
    const [response, setResponse] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const { isPermissionValid } = useContext(AuthenticationContext);
    const { getDatabaseStructure } = useGetDatabaseStructureService();
    const { updatePermissions } = useUpdatePermissions();

    const {
        selected: { selectedAccount, selectedServer },
        URL
    } = useContext(GuiContext);

    const importDatabase = async () => {

        if (!isPermissionValid('active')) {
            if (!testPrivateKey(private_key)) {
                setError('Invalid private key!');
                return;
            }
        }

        if (!redis_host) {
            setError('Please provide the Redis host!');
            return;
        }

        if (!validateURL(redis_host)) {
            setError("Invalid Redis host!");
            return;
        }

        if (!redis_port) {
            setError('Please provide the Redis port!');
            return;
        }

        if (!composition) {
            setError('Please provide the Composition!');
            return;
        }

        setLoading(true);

        try {
            const res = await fetch(`${URL}/api/plugins/redis_import`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify({
                    database: selectedAccount || "",
                    permission: "active",
                    server_url: selectedServer || "",
                    private_key: private_key,
                    redis_host: redis_host,
                    redis_port: redis_port,
                    redis_password: redis_password,
                    composition: composition,
                    exist: false,
                    scope: selectedAccount || "",
                }),
            });

            const data = await res.json();
            setResponse(data);
            getDatabaseStructure();
            updatePermissions();
            if (!data?.status) {
                setError(data?.message);
            }
        } catch (err) {
            setError(err.message || 'An error occurred');
        } finally {
            setLoading(false);
        }
    };

    const resetServerStatus = () => {
        setLoading(initialServerStatus.loading);
        setResponse(initialServerStatus.response);
        setError(initialServerStatus.error);
    }

    return {
        response,
        loading,
        error,
        importDatabase,
        resetServerStatus
    };
};

export const useImportMongoDatabase = ({
    private_key,
    mongo_username,
    mongo_password,
    mongo_host,
    mongo_port,
    mongo_database,
    mongo_collection,
    composition
}) => {
    const [response, setResponse] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const { isPermissionValid } = useContext(AuthenticationContext);
    const { getDatabaseStructure } = useGetDatabaseStructureService();
    const { updatePermissions } = useUpdatePermissions();

    const {
        selected: { selectedAccount, selectedServer },
        URL
    } = useContext(GuiContext);

    const importDatabase = async () => {

        // Validations -->

        if (!isPermissionValid('active')) {
            if (!testPrivateKey(private_key)) {
                setError('Invalid private key!');
                return;
            }
        }

        if (!mongo_host) {
            setError('Please provide the Mongo host!');
            return;
        }

        // if (!validateURL(mongo_host)) {
        //     setError("Invalid Mongo host!");
        //     return;
        // }

        if (!mongo_port) {
            setError('Please provide the Mogo port!');
            return;
        }

        if (!mongo_username) {
            setError('Please provide the Mongo username!');
            return;
        }

        if (!mongo_password) {
            setError('Please provide the Mongo password!');
            return;
        }

        if (!mongo_database) {
            setError('Please provide the Mongo database!');
            return;
        }

        if (!mongo_collection) {
            setError('Please provide the Mongo collection!')
        }

        if (!composition) {
            setError('Please provide the Composition!');
            return;
        }
        setLoading(true);

        try {
            const res = await fetch(`${URL}/api/plugins/mongo_import`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify({
                    database: selectedAccount || "",
                    permission: "active",
                    server_url: selectedServer || "",
                    private_key: private_key,
                    mongo_username: mongo_username,
                    mongo_password: mongo_password,
                    mongo_host: mongo_host,
                    mongo_port: mongo_port,
                    mongo_database: mongo_database,
                    mongo_collection: mongo_collection,
                    composition: composition,
                    exist: false,
                    scope: selectedAccount || "",
                }),
            });

            const data = await res.json();
            setResponse(data);
            getDatabaseStructure();
            updatePermissions();
            if (!data?.status) {
                setError(data?.message);
            }
        } catch (err) {
            setError(err.message || 'An error occurred');
        } finally {
            setLoading(false);
        }
    };

    const resetServerStatus = () => {
        setLoading(initialServerStatus.loading);
        setResponse(initialServerStatus.response);
        setError(initialServerStatus.error);
    }

    return {
        response,
        loading,
        error,
        importDatabase,
        resetServerStatus
    };
};

export const useBackupDatabase = () => {
    const [response, setResponse] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const {
        selected: { selectedAccount, selectedServer },
        URL
    } = useContext(GuiContext);

    const createBackup = async (options = {}) => {
        setLoading(true);
        setError(null);

        try {
            const res = await fetch(`${URL}/api/plugins/backup`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify({
                    database: selectedAccount || "",
                    server_url: selectedServer || "",
                    options: {
                        full_backup: options.full_backup ?? true,
                        compositions: options.compositions || [],
                        remote: options.remote || false,
                        method: options.method,
                        host: options.host,
                        user: options.user,
                        password: options.password,
                        path: options.path
                    }
                }),
            });

            const data = await res.json();
            setResponse(data);
            if (!data?.status) {
                setError(data?.message || data?.error || 'Backup failed');
            }
        } catch (err) {
            setError(err.message || 'An error occurred');
        } finally {
            setLoading(false);
        }
    };

    const resetServerStatus = () => {
        setLoading(initialServerStatus.loading);
        setResponse(initialServerStatus.response);
        setError(initialServerStatus.error);
    }

    return {
        response,
        loading,
        error,
        createBackup,
        resetServerStatus
    };
};

export const useUpdateDatabaseStructure = () => {
    const [response, setResponse] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const { setDatabaseStructure, setDatabaseStructureLoading, setActiveComposition } = useContext(TableContext);
    const { getCompositionsObjectStructure } = useGetCompositionsObjectStructure();

    const {
        selected: { selectedAccount, selectedServer },
        URL
    } = useContext(GuiContext);

    const updateDatabaseStructure = async () => {
        setLoading(true);
        setDatabaseStructureLoading(true);
        try {
            const res = await fetch(`${URL}/api/database/get_database_structure`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify({
                    account_name: selectedAccount || "",
                    server_url: selectedServer || "",
                }),
            });

            const data = await res.json();
            setResponse(data);
            setDatabaseStructure(data);
            getCompositionsObjectStructure();
            setActiveComposition(data?.tables[0]?.name);
            if (!data?.status) {
                setError(data?.message);
            }
        } catch (err) {
            setError(err.message || 'An error occurred');
        } finally {
            setLoading(false);
            setDatabaseStructureLoading(false);
        }
    }

    return {
        response,
        loading,
        error,
        updateDatabaseStructure
    }
}

export const useGetCompositionsObjectStructure = () => {
    const [response, setResponse] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const { setCompositionsObjectStructure } = useContext(TableContext);

    const {
        selected: { selectedAccount, selectedServer },
        URL
    } = useContext(GuiContext);

    const getCompositionsObjectStructure = async () => {
        setLoading(true);
        try {
            const res = await fetch(`${URL}/api/database/get_compositions_object_structure`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify({
                    account_name: selectedAccount || "",
                    server_url: selectedServer || "",
                }),
            });

            const data = await res.json();
            setResponse(data);
            setCompositionsObjectStructure(data);
            if (!data?.status) {
                setError(data?.message);
            }
        } catch (err) {
            setError(err.message || 'An error occurred');
        } finally {
            setLoading(false);
        }
    }

    return {
        response,
        loading,
        error,
        getCompositionsObjectStructure
    }
}